import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { FilterProvider, useFilters } from '../contexts/FilterContext';
import { useAuth } from '../hooks/useAuth';
import { useCategories } from '../hooks/useCategories';

interface MainLayoutProps {
  children: React.ReactNode;
}

function MainLayoutContent({ children }: MainLayoutProps) {
  const { user, signOut, loading, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { activeFilter, setActiveFilter, activeEventFilter, setActiveEventFilter, activeCategoryFilter, setActiveCategoryFilter } = useFilters();
  const { categories } = useCategories({ activeOnly: true });

  const isHomePage = location.pathname === '/';

  const handleMobileFilterClick = (filter: string) => {
    if (!isHomePage) {
      navigate('/');
    }
    setActiveEventFilter(filter);
    setIsMobileMenuOpen(false);
  };

  const handleMobileCategoryClick = (categoryId: string | null) => {
    if (!isHomePage) {
      navigate('/');
    }
    setActiveCategoryFilter(categoryId);
    setIsMobileMenuOpen(false);
  };

  const isActiveRoute = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className="h-screen bg-cream-light flex flex-col">
      {/* Header */}
      <header className="bg-cream shadow-sm border-b border-gray-200 flex-shrink-0">
        <div className="flex">
          {/* Left section - aligned with sidebar */}
          <div className="hidden xl:flex xl:w-80 items-center justify-center px-6 h-16 border-r border-gray-200">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/daily-do-horizontal.png" 
                alt="The Daily Do" 
                className="h-8 w-auto rounded-lg object-contain"
              />
            </Link>
          </div>
          
          {/* Right section - main content area */}
          <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-0">
            <div className="flex justify-between items-center h-16 xl:justify-end">
              {/* Mobile/tablet logo */}
              <Link to="/" className="flex items-center space-x-2 xl:hidden">
                <img
                  src="/daily-do-horizontal.png"
                  alt="The Daily Do"
                  className="h-8 w-auto rounded-lg object-contain"
                />
              </Link>

            {/* User Menu */}
            <div className="flex items-center space-x-3">
              {loading ? (
                <div className="w-8 h-8 animate-pulse bg-gray-200 rounded-full"></div>
              ) : user ? (
                <Link
                  to="/profile"
                  className="xl:hidden flex items-center justify-center w-9 h-9 rounded-full bg-primary-100 hover:bg-primary-200 transition-colors"
                >
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt="Profile"
                      className="w-9 h-9 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  )}
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Sign In
                </Link>
              )}

              {/* Mobile menu button */}
              <button
                className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Overlay */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Slide-in Menu */}
            <div className="fixed inset-0 sm:inset-y-0 sm:right-0 sm:left-auto sm:max-w-sm bg-white z-50 md:hidden shadow-xl">
              <div className="h-full flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-4 py-6">
                  <div className="space-y-6">
                    {/* Create Event Button */}
                    {user && (
                      <Link
                        to="/add-event"
                        className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Create Event
                      </Link>
                    )}

                    {/* Discover Section */}
                    <div>
                      <h3 className="px-2 mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Discover
                      </h3>
                      <nav className="space-y-1">
                        <Link
                          to="/"
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                            isActiveRoute('/') && !isActiveRoute('/feed') && !isActiveRoute('/blogs')
                              ? 'text-primary-600 bg-primary-50'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Events
                        </Link>
                        {user && (
                          <Link
                            to="/feed"
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                              isActiveRoute('/feed')
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Activity Feed
                          </Link>
                        )}
                        <Link
                          to="/blogs"
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                            isActiveRoute('/blogs')
                              ? 'text-primary-600 bg-primary-50'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                          </svg>
                          Event Blogs
                        </Link>
                      </nav>
                    </div>

                    {/* Event Filters Section */}
                    <div className="pt-4 border-t border-gray-200">
                      <h3 className="px-2 mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Event Filters
                      </h3>
                      <nav className="space-y-1">
                        {['All Events', 'Free Events', 'Today', 'This Weekend'].map((filter) => (
                          <button
                            key={filter}
                            onClick={() => handleMobileFilterClick(filter)}
                            className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                              isHomePage && activeEventFilter === filter
                                ? 'text-primary-600 bg-primary-50'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {filter}
                          </button>
                        ))}
                      </nav>
                    </div>

                    {/* Categories Section */}
                    {categories.length > 0 && (
                      <div className="pt-4 border-t border-gray-200">
                        <h3 className="px-2 mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Categories
                        </h3>
                        <nav className="space-y-1">
                          {categories.map((category) => (
                            <button
                              key={category.id}
                              onClick={() => handleMobileCategoryClick(
                                activeCategoryFilter === category.id ? null : category.id!
                              )}
                              className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                                isHomePage && activeCategoryFilter === category.id
                                  ? 'text-primary-600 bg-primary-50'
                                  : 'text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {category.icon && <span>{category.icon}</span>}
                              {category.name}
                            </button>
                          ))}
                        </nav>
                      </div>
                    )}

                    {/* Legal Links */}
                    {user && (
                      <div className="pt-4 border-t border-gray-200">
                        <h3 className="px-2 mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Legal
                        </h3>
                        <nav className="space-y-1">
                          <Link
                            to="/terms"
                            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Terms & Conditions
                          </Link>
                          <Link
                            to="/privacy"
                            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            Privacy Policy
                          </Link>
                        </nav>
                      </div>
                    )}

                    {/* Admin Navigation */}
                    {isAdmin && (
                      <div className="pt-4 border-t border-gray-200">
                        <h3 className="px-2 mb-3 text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-2">
                          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          Admin
                        </h3>
                        <nav className="space-y-1">
                          <Link
                            to="/admin/blog"
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                              isActiveRoute('/admin/blog')
                                ? 'text-amber-700 bg-amber-50'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Manage Blogs
                          </Link>
                          <Link
                            to="/admin/moderation"
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium ${
                              isActiveRoute('/admin/moderation')
                                ? 'text-amber-700 bg-amber-50'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            Moderation
                          </Link>
                        </nav>
                      </div>
                    )}

                    {/* User Section */}
                    {user ? (
                      <div className="pt-4 border-t border-gray-200">
                        <div className="px-3 py-2 mb-2">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {user.displayName || user.email}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {user.email}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            handleSignOut();
                            setIsMobileMenuOpen(false);
                          }}
                          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-pink-600 hover:bg-pink-50 transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                          </svg>
                          Sign Out
                        </button>
                      </div>
                    ) : !loading && (
                      <div className="pt-4 border-t border-gray-200">
                        <div className="bg-primary-50 rounded-xl p-4 text-center">
                          <p className="text-sm text-primary-700 mb-3">
                            Sign in to create events and manage your profile
                          </p>
                          <Link
                            to="/login"
                            className="block w-full px-4 py-2.5 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Sign In
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        </div>
      </header>

      {/* Main Content with Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        <Sidebar 
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
        />
        <main className="flex-1 overflow-y-auto flex justify-center">
          <div className="w-full max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export function MainLayout({ children }: MainLayoutProps) {
  return (
    <FilterProvider>
      <MainLayoutContent>{children}</MainLayoutContent>
    </FilterProvider>
  );
}